import React, { useEffect, useState } from "react";
import { useAvatarCache } from "../../../contexts/AvatarContext";
import DefaultAvatar from "./DefaultAvatar";
import AlternativeDefaultAvatar from "./AlternativeDefaultAvatar";

type AvatarSize = "xxxs" | "xxs" | "xs" | "sm" | "md" | "lg" | "xl";

interface AvatarImageProps {
  userId: string;
  displayName?: string;
  size?: AvatarSize;
  className?: string;
  alternative?: boolean;
  /** Disable a separate retry action when the image decorates another control. */
  retryable?: boolean;
  /** @deprecated Use `alternative` for the rounded-square variant. */
  alternate?: boolean;
}

const sizeMap: Record<AvatarSize, string> = {
  xxxs: "w-6 h-6", // 24px
  xxs: "w-8 h-8", // 32px
  xs: "w-10 h-10", // 40px
  sm: "w-12 h-12", // 48px
  md: "w-20 h-20",
  lg: "w-32 h-32",
  xl: "w-48 h-48",
};

const AvatarImage: React.FC<AvatarImageProps> = ({
  userId,
  displayName,
  size = "md",
  className,
  alternative,
  retryable = true,
  alternate = false
}) => {
  const { getAvatarUrl, refreshAvatar } = useAvatarCache();
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = await getAvatarUrl(userId);
        if (active) {
          setAvatarSrc(url || null);
        }
      } catch (err) {
        if (active) {
          setError("Failed to load avatar");
          setAvatarSrc(null);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [userId, getAvatarUrl, retryCount]);

  const handleRetry = () => {
    refreshAvatar(userId);
    setRetryCount(prev => prev + 1);
  };

  // Keep the former prop as an alias so existing avatar callers remain compatible.
  const useAlternativeShape = alternative ?? alternate;
  const shapeClasses = useAlternativeShape
    ? "!rounded-lg overflow-hidden border-0 shadow-none"
    : "!rounded-full";

  const sizeClasses = `avatar-image ${shapeClasses} object-cover ${sizeMap[size]} ${className || ""}`;

  if (isLoading) {
    return (
      <div className={`${sizeClasses} bg-gray-200 dark:bg-[#2c3140] animate-pulse flex items-center justify-center`}>
        <span className="text-gray-500 dark:text-slate-500 text-xs font-medium">...</span>
      </div>
    );
  }

  if (error) {
    if (!retryable) {
      return useAlternativeShape ? (
        <AlternativeDefaultAvatar userId={userId} displayName={displayName} size={size} />
      ) : (
        <DefaultAvatar userId={userId} displayName={displayName} size={size} />
      );
    }
    if (useAlternativeShape) {
      return (
        <button
          type="button"
          onClick={handleRetry}
          className="border-0 bg-transparent p-0"
          title="Avatar failed to load. Click to retry."
        >
          <AlternativeDefaultAvatar userId={userId} displayName={displayName} size={size} />
        </button>
      );
    }

    return (
      <div
        className={`${sizeClasses} bg-red-50 border-red-200 flex items-center justify-center cursor-pointer`}
        onClick={handleRetry}
        title="Click to retry"
      />
    );
  }

  if (avatarSrc) {
    return (
      <img
        src={avatarSrc}
        alt=""
        className={sizeClasses}
        onError={() => setAvatarSrc(null)}
      />
    );
  }

  return useAlternativeShape ? (
    <AlternativeDefaultAvatar userId={userId} displayName={displayName} size={size} />
  ) : (
    <DefaultAvatar userId={userId} displayName={displayName} size={size} />
  );
};

export default AvatarImage;
